import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { exportQuotationPdf, openQuotationPreview } from '../utils/quotationTemplate';
import { useSettings } from '../context/SettingsContext';

const fmtMoney = (amount, currency = 'USD') => `${currency} ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Quotations = () => {
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const { settings } = useSettings();

  const load = async () => {
    try {
      const [q, c] = await Promise.all([
        axios.get('/api/quotations'),
        canManage ? axios.get('/api/clients') : Promise.resolve({ data: { data: [] } }),
      ]);
      setQuotations(q.data.data);
      setClients(c.data.data);
    } catch (err) { showToast(err.response?.data?.message || 'Failed to load quotations', 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createFromClient = async () => {
    if (!selectedClient) return showToast('Choose a client first', 'error');
    try {
      await axios.post('/api/quotations', {
        clientId: selectedClient,
        status: 'draft',
        terms: 'This quotation is valid for the stated period and does not include additional out-of-scope work.',
      });
      showToast('Quotation created', 'success');
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to create quotation', 'error'); }
  };

  const setStatus = async (q, status) => {
    try {
      await axios.put(`/api/quotations/${q.id}`, { status });
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to update quotation', 'error'); }
  };

  const convert = async (q) => {
    try {
      await axios.post(`/api/quotations/${q.id}/convert-to-invoice`, {});
      showToast('Converted to invoice', 'success');
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to convert quotation', 'error'); }
  };

  const respond = async (q, status) => {
    const comment = window.prompt(status === 'accepted' ? 'Approval comment (optional)' : 'Rejection reason (optional)', '');
    try {
      await axios.post(`/api/quotations/${q.id}/respond`, { status, comment });
      showToast(status === 'accepted' ? 'Quotation accepted' : 'Quotation rejected', 'success');
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Failed to respond', 'error'); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quotationNumber}?`)) return;
    try { await axios.delete(`/api/quotations/${q.id}`); load(); }
    catch (err) { showToast(err.response?.data?.message || 'Failed to delete quotation', 'error'); }
  };

  const downloadPdf = async (q) => {
    try {
      await exportQuotationPdf({ quotation: q, client: q.client, items: q.items, totals: q, settings });
    } catch (err) {
      showToast('Failed to export PDF', 'error');
    }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      <div className="flex-between mb-24">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Quotations</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{quotations.length} quotations</p>
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select className="input select" style={{ width: 220 }} value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
              <option value="">Choose client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
            </select>
            <button className="btn btn-primary" onClick={createFromClient}><Plus size={15} /> New Quotation</button>
          </div>
        )}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>#</th><th>Client</th><th>Status</th><th>Issue</th><th>Valid Until</th><th>Total</th><th>Actions</th></tr></thead>
            <tbody>
              {quotations.map(q => (
                <tr key={q.id}>
                  <td style={{ fontWeight: 700 }}>{q.quotationNumber}</td>
                  <td>{q.client?.companyName || '-'}</td>
                  <td><span className={`badge ${q.status === 'accepted' || q.status === 'converted' ? 'badge-green' : q.status === 'rejected' ? 'badge-red' : 'badge-purple'}`}>{q.status}</span></td>
                  <td>{q.issueDate || '-'}</td>
                  <td>{q.validUntil || '-'}</td>
                  <td style={{ fontWeight: 700 }}>{fmtMoney(q.total, q.currency)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openQuotationPreview({ quotation: q, client: q.client, items: q.items, totals: q, settings })}><FileText size={13} /> Preview</button>
                      <button className="btn btn-primary btn-sm" onClick={() => downloadPdf(q)}><FileText size={13} /> Export PDF</button>
                      {canManage && q.status !== 'converted' && <button className="btn btn-secondary btn-sm" onClick={() => convert(q)}><RefreshCw size={13} /> Invoice</button>}
                      {canManage && q.status === 'draft' && <button className="btn btn-secondary btn-sm" onClick={() => setStatus(q, 'sent')}>Send</button>}
                      {canManage && q.status !== 'accepted' && <button className="btn btn-secondary btn-sm" onClick={() => setStatus(q, 'accepted')}>Accept</button>}
                      {!canManage && q.status === 'sent' && <button className="btn btn-primary btn-sm" onClick={() => respond(q, 'accepted')}>Approve</button>}
                      {!canManage && q.status === 'sent' && <button className="btn btn-secondary btn-sm" onClick={() => respond(q, 'rejected')}>Reject</button>}
                      {canManage && <button onClick={() => remove(q)} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!quotations.length && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No quotations yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Quotations;
